import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  Section,
  Inner,
  SectionHeader,
  SectionTitle,
  SectionSubtitle,
  Divider,
  Badge,
  fadeInUp,
} from '../styles/v2.styled';
import ImgNode from '../../image/NodeMaster.jpg';
import ImgNodeFind from '../../image/NodeMaster-Find.jpg';
import ImgNodeMemo from '../../image/NodeMaster-Memo.jpg';
import ImgNodeCheck from '../../image/NodeMaster-Check.jpg';
import ImgKetoSecret from '../../image/KetoSecret4.png';
import ImgHelperDoc from '../../image/HelperDoc1.png';
import ImgMobileMain from '../../image/앤츠네스트앱.png';
import ImgMobileSub from '../../image/antsnest.png';
import FileKetoSecret from '../../ftp/KetoSecret.msi';
import FileSkyApp from '../../ftp/SkyApp.apk';

const WINDOWS_ITEMS = [
  {
    name: 'Node Master',
    label: '윈도우 앱',
    summary: '개발 자료, 메모, 체크리스트를 한 곳에서 관리하는 로컬 학습 도구입니다.',
    version: '0.01',
    date: '2020-10-08',
    license: '무료 소프트웨어',
    image: ImgNode,
    downloads: [
      { label: '클라우드 설치 파일', href: '/files/NodeMaster.msi', fileName: 'NodeMaster.msi' },
      { label: '로컬 설치 파일', href: '/files/NodeMasterLocal.msi', fileName: 'NodeMasterLocal.msi' },
    ],
    screenshots: [ImgNode, ImgNodeFind, ImgNodeMemo, ImgNodeCheck],
  },
  {
    name: 'Keto Secret',
    label: '윈도우 앱',
    summary: '식단 기록과 관리 흐름을 확인할 수 있는 데스크톱 애플리케이션입니다.',
    version: '0.01',
    date: '2020-10-08',
    license: '무료 소프트웨어',
    image: ImgKetoSecret,
    downloads: [
      { label: 'Windows 설치 파일', href: FileKetoSecret, fileName: 'KetoSecret.msi' },
    ],
    screenshots: [ImgKetoSecret],
  },
  {
    name: 'Helper Doc',
    label: '문서 도구',
    summary: '학습 노트와 작업 문서를 빠르게 정리하기 위한 보조 도구입니다.',
    version: '준비중',
    date: '배포 일정 조율중',
    license: '무료 예정',
    image: ImgHelperDoc,
    downloads: [],
    screenshots: [ImgHelperDoc],
  },
];

const MOBILE_ITEMS = [
  {
    name: 'Antsnest App',
    label: '모바일 앱',
    summary: '모바일에서 강의 공지와 학습 자료를 확인하기 위한 앱입니다.',
    version: '0.01',
    date: '2020-10-08',
    license: '개발중',
    image: ImgMobileMain,
    downloads: [
      { label: 'Android APK', href: FileSkyApp, fileName: 'SkyApp.apk' },
    ],
    screenshots: [ImgMobileMain, ImgMobileSub],
  },
];

const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.neutral[0]};
`;

const Hero = styled.section`
  background: linear-gradient(135deg, #052f36 0%, #076475 55%, #0f9bb0 100%);
  color: #fff;
  padding: 72px 0 56px;
`;

const HeroInner = styled(Inner)`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(280px, 0.9fr);
  gap: 44px;
  align-items: center;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const HeroCopy = styled.div`
  animation: ${fadeInUp} 0.5s ease both;
`;

const Eyebrow = styled.div`
  width: 48px;
  height: 4px;
  border-radius: 2px;
  background: #67e8f9;
  margin-bottom: 20px;
`;

const HeroTitle = styled.h1`
  margin: 0 0 14px;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  line-height: 1.15;
  color: #fff;
`;

const HeroText = styled.p`
  max-width: 640px;
  margin: 0;
  color: rgba(255, 255, 255, 0.86);
  font-size: 1.04rem;
  line-height: 1.7;
`;

const HeroPanel = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 24px;
  animation: ${fadeInUp} 0.5s 0.08s ease both;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled.div`
  padding: 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
`;

const StatValue = styled.div`
  font-size: 1.35rem;
  font-weight: 900;
`;

const StatLabel = styled.div`
  margin-top: 4px;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.8rem;
`;

const TabBar = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 34px;
  flex-wrap: wrap;
`;

const TabLink = styled(Link)`
  min-width: 132px;
  min-height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary[500] : theme.colors.neutral[200])};
  background: ${({ $active, theme }) => ($active ? theme.colors.primary[50] : theme.colors.neutral[0])};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  -webkit-text-fill-color: ${({ $active, theme }) => ($active ? theme.colors.primary[700] : theme.colors.neutral[700])};
  text-decoration: none;
  font-weight: 900;
`;

const ProductList = styled.div`
  display: grid;
  gap: 22px;
`;

const Product = styled.article`
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 24px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  border-radius: 8px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.neutral[0]};
  animation: ${fadeInUp} 0.45s ease both;
  animation-delay: ${({ $idx }) => $idx * 0.06}s;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const Preview = styled.div`
  min-height: 240px;
  background:
    linear-gradient(135deg, rgba(8, 145, 178, 0.12), rgba(15, 23, 42, 0.04)),
    ${({ theme }) => theme.colors.neutral[50]};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
`;

const PreviewImage = styled.img`
  width: 100%;
  max-height: 260px;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 14px 34px rgba(15, 23, 42, 0.12);
`;

const ProductBody = styled.div`
  padding: 26px 26px 24px 0;

  @media (max-width: 820px) {
    padding: 0 22px 24px;
  }
`;

const ProductTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const ProductTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.neutral[900]};
  font-size: 1.35rem;
  font-weight: 900;
`;

const ProductSummary = styled.p`
  color: ${({ theme }) => theme.colors.neutral[600]};
  line-height: 1.65;
  margin: 0 0 18px;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Meta = styled.div`
  border-left: 3px solid ${({ theme }) => theme.colors.primary[500]};
  padding: 6px 0 6px 10px;
  background: ${({ theme }) => theme.colors.neutral[50]};
`;

const MetaLabel = styled.div`
  color: ${({ theme }) => theme.colors.neutral[400]};
  font-size: 0.76rem;
  font-weight: 800;
`;

const MetaValue = styled.div`
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.neutral[800]};
  font-size: 0.9rem;
  font-weight: 800;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const DownloadButton = styled.a`
  min-height: 40px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary[700]};
  color: #fff;
  -webkit-text-fill-color: #fff;
  text-decoration: none;
  font-weight: 900;
  font-size: 0.88rem;
`;

const DisabledButton = styled.span`
  min-height: 40px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[200]};
  color: ${({ theme }) => theme.colors.neutral[500]};
  font-weight: 900;
  font-size: 0.88rem;
`;

const Screens = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-top: 20px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const ScreenThumb = styled.img`
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.neutral[100]};
`;

function DownloadPage({ type = 'windows' }) {
  const isMobile = type === 'mobile';
  const items = isMobile ? MOBILE_ITEMS : WINDOWS_ITEMS;

  return (
    <PageWrapper>
      <Hero>
        <HeroInner>
          <HeroCopy>
            <Eyebrow />
            <HeroTitle>자료실</HeroTitle>
            <HeroText>
              설치 파일, 학습 보조 도구, 모바일 앱 자료를 정리했습니다.
              필요한 파일을 빠르게 찾고 최신 배포 상태를 확인할 수 있습니다.
            </HeroText>
          </HeroCopy>
          <HeroPanel>
            <StatGrid>
              <Stat>
                <StatValue>4</StatValue>
                <StatLabel>제공 자료</StatLabel>
              </Stat>
              <Stat>
                <StatValue>2</StatValue>
                <StatLabel>지원 플랫폼</StatLabel>
              </Stat>
              <Stat>
                <StatValue>Free</StatValue>
                <StatLabel>기본 라이선스</StatLabel>
              </Stat>
            </StatGrid>
          </HeroPanel>
        </HeroInner>
      </Hero>

      <Section $py="64px" $pyMobile="44px">
        <Inner>
          <SectionHeader>
            <Divider $center />
            <SectionTitle>{isMobile ? '모바일 자료' : '윈도우 자료'}</SectionTitle>
            <SectionSubtitle>검증된 파일과 주요 화면을 함께 확인하세요.</SectionSubtitle>
          </SectionHeader>

          <TabBar>
            <TabLink to="/Download" $active={!isMobile}>윈도우 앱</TabLink>
            <TabLink to="/DownloadMobile" $active={isMobile}>모바일 앱</TabLink>
          </TabBar>

          <ProductList>
            {items.map((item, index) => (
              <Product key={item.name} $idx={index}>
                <Preview>
                  <PreviewImage src={item.image} alt={`${item.name} 미리보기`} />
                </Preview>
                <ProductBody>
                  <ProductTop>
                    <ProductTitle>{item.name}</ProductTitle>
                    <Badge $variant={item.downloads.length ? 'success' : 'warning'}>{item.label}</Badge>
                  </ProductTop>
                  <ProductSummary>{item.summary}</ProductSummary>

                  <MetaGrid>
                    <Meta>
                      <MetaLabel>버전</MetaLabel>
                      <MetaValue>{item.version}</MetaValue>
                    </Meta>
                    <Meta>
                      <MetaLabel>배포일</MetaLabel>
                      <MetaValue>{item.date}</MetaValue>
                    </Meta>
                    <Meta>
                      <MetaLabel>라이선스</MetaLabel>
                      <MetaValue>{item.license}</MetaValue>
                    </Meta>
                  </MetaGrid>

                  <Actions>
                    {item.downloads.length > 0 ? item.downloads.map((download) => (
                      <DownloadButton
                        key={download.label}
                        href={download.href}
                        download={download.fileName}
                      >
                        {download.label}
                      </DownloadButton>
                    )) : (
                      <DisabledButton>배포 준비중</DisabledButton>
                    )}
                  </Actions>

                  <Screens>
                    {item.screenshots.map((shot, shotIndex) => (
                      <ScreenThumb
                        key={`${item.name}-${shotIndex}`}
                        src={shot}
                        alt={`${item.name} 화면 ${shotIndex + 1}`}
                        loading="lazy"
                      />
                    ))}
                  </Screens>
                </ProductBody>
              </Product>
            ))}
          </ProductList>
        </Inner>
      </Section>
    </PageWrapper>
  );
}

export default DownloadPage;
